import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { AbTestingApiService } from '@app/pages/checkout-app/services/api/abtesting.api.service';
import { SettingsService } from '@app/services/settings/settings.service';
import { TimeUnits } from '@utils/times-utils';
import {
  BillingPlanApiResponse,
  ImpactOverviewApiResponse,
  OverviewMetrics,
} from '@app/pages/impact-dashboard/impact-dashboard-metric.models';

@Injectable({ providedIn: 'root' })
export class ImpactMetricsService {
  constructor(
    private readonly abTestingApiService: AbTestingApiService,
    private readonly settingsService: SettingsService,
  ) {}

  formatCurrency(value: number | null | undefined, currencySymbol: string): string {
    if (value == null) return `${currencySymbol}0`;
    return `${currencySymbol}${Math.round(value).toLocaleString('en-US')}`;
  }

  formatRoi(value: number | null | undefined): string {
    if (value == null) return '0x';
    return `${value.toFixed(1)}x`;
  }

  formatPercentage(value: number | null | undefined): string {
    if (value == null) return '0%';
    return `${value.toFixed(0)}%`;
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  calculateHideRoi(billingPlan: BillingPlanApiResponse | null): boolean {
    if (!billingPlan?.startDate) return false;
    const activatedAt = billingPlan.startDate instanceof Date ? billingPlan.startDate : new Date(billingPlan.startDate);
    const daysSinceActivation = Math.floor((Date.now() - activatedAt.getTime()) / TimeUnits._MS_IN_DAY);
    return daysSinceActivation > 365;
  }

  mapOverviewMetrics(raw: ImpactOverviewApiResponse, currencySymbol: string): OverviewMetrics {
    return {
      annualizedRevenueUplift: this.formatCurrency(raw?.AB_TEST_ANNUALIZE_IMPACT_REVENUE, currencySymbol),
      roi: this.formatRoi(raw?.ROI_RATIO_ONLY_AB_TEST),
      winRate: this.formatPercentage(raw?.WIN_RATE),
      concludedTests: raw?.CONCLUDED_TEST ?? 0,
      winningTests: raw?.WINNING_TEST ?? 0,
      activeSegments: raw?.COUNT_UNIQUE_SEGMENTS ?? 0,
      implementedComponents: raw?.UNIQUE_EXTENSIONS_COUNT ?? 0,
      runningTests: 0,
    };
  }

  loadOverviewMetricsWithBillingCheck(
    currencySymbol: string,
  ): Observable<{ hideRoi: boolean; metrics: OverviewMetrics | null }> {
    return forkJoin({
      billingPlan: this.settingsService.getFirstActivatedBillingPlan().pipe(
        timeout(5000),
        catchError(() => of(null)),
      ),
      rawMetrics: this.abTestingApiService.get('ab-tests/impact-overview-metric').pipe(catchError(() => of(null))),
    }).pipe(
      map(({ billingPlan, rawMetrics }) => ({
        hideRoi: this.calculateHideRoi(billingPlan),
        metrics: rawMetrics ? this.mapOverviewMetrics(rawMetrics, currencySymbol) : null,
      })),
    );
  }
}
